import { cn } from "@/lib/utils"

interface GirlAvatarProps {
  size?: number
  className?: string
  /** When true the mouth animates as if she is speaking. */
  talking?: boolean
}

/**
 * Riya — Enga's AI sales assistant. An inline SVG redhead girl with a headset
 * (support-assistant persona). The mouth is a group we can animate for a
 * "talking" effect, and the whole head bobs gently on hover.
 */
export function GirlAvatar({ size = 40, className, talking = false }: GirlAvatarProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("select-none", className)}
      role="img"
      aria-label="Enga AI assistant"
    >
      <defs>
        <linearGradient id="riya-hair" x1="30" y1="8" x2="90" y2="60" gradientUnits="userSpaceOnUse">
          <stop stopColor="#f07a2e" />
          <stop offset="0.55" stopColor="#e3541c" />
          <stop offset="1" stopColor="#c13a10" />
        </linearGradient>
        <linearGradient id="riya-headset" x1="20" y1="0" x2="100" y2="70" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2b2b33" />
          <stop offset="1" stopColor="#131318" />
        </linearGradient>
        <radialGradient id="riya-cheek" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffb3a6" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffb3a6" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Back hair — long flowing strands behind the head */}
      <path
        d="M30 46c-8 10-11 30-7 46 3 13 9 21 15 18 6-3 5-14 6-25 1-12 5-20 12-24"
        fill="url(#riya-hair)"
      />
      <path
        d="M90 46c8 10 11 30 7 46-3 13-9 21-15 18-6-3-5-14-6-25-1-12-5-20-12-24"
        fill="url(#riya-hair)"
      />

      {/* Headset band */}
      <path
        d="M22 34c8-16 28-26 38-26s30 10 38 26"
        stroke="url(#riya-headset)"
        strokeWidth="7"
        strokeLinecap="round"
      />

      {/* Face base */}
      <ellipse cx="60" cy="60" rx="38" ry="41" fill="#ffd9c2" />

      {/* Ears */}
      <ellipse cx="22" cy="66" rx="7" ry="10" fill="#f4c09e" />
      <ellipse cx="98" cy="66" rx="7" ry="10" fill="#f4c09e" />

      {/* Headset ear cups */}
      <rect x="10" y="52" width="17" height="34" rx="8.5" fill="url(#riya-headset)" />
      <rect x="93" y="52" width="17" height="34" rx="8.5" fill="url(#riya-headset)" />
      {/* Mic boom */}
      <path
        d="M102 60c5 2 8 7 7 12-1 4-4 7-8 7"
        stroke="url(#riya-headset)"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="101" cy="79" r="3.4" fill="#2b2b33" />

      {/* Hair — top volume */}
      <path
        d="M22 52c0-20 15-32 38-32s38 12 38 32c0-14-10-20-20-22-2-8-9-14-18-14s-16 6-18 14c-10 2-20 8-20 22z"
        fill="url(#riya-hair)"
      />
      <path d="M20 50c6-16 22-26 40-26 18 0 34 10 40 26-4-11-14-17-22-19 2-6 9-8 9-8-8 2-12 6-14 12-12-4-26-4-38 0-2-6-6-10-14-12 0 0 7 2 9 8-8 2-18 8-22 19z" fill="#e3541c" opacity="0.55" />
      {/* Fringe strands */}
      <path d="M34 46c3-8 10-12 16-12" stroke="#c13a10" strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M86 46c-3-8-10-12-16-12" stroke="#c13a10" strokeWidth="3" strokeLinecap="round" fill="none" />

      {/* Eyes */}
      <g>
        <ellipse cx="46" cy="60" rx="6" ry="7" fill="#2c2320" />
        <ellipse cx="74" cy="60" rx="6" ry="7" fill="#2c2320" />
        <circle cx="48" cy="57" r="2" fill="#ffffff" />
        <circle cx="76" cy="57" r="2" fill="#ffffff" />
      </g>

      {/* Eyebrows */}
      <path d="M36 50c4-3 9-3 13-1" stroke="#8a3b16" strokeWidth="2.4" strokeLinecap="round" fill="none" />
      <path d="M71 49c4-2 9-2 13 1" stroke="#8a3b16" strokeWidth="2.4" strokeLinecap="round" fill="none" />

      {/* Blush */}
      <ellipse cx="38" cy="72" rx="8" ry="5" fill="url(#riya-cheek)" />
      <ellipse cx="82" cy="72" rx="8" ry="5" fill="url(#riya-cheek)" />

      {/* Mouth — animates when talking */}
      <g className={cn(talking && "riya-talking")}>
        <path
          d="M52 74c2.4 4.5 13.6 4.5 16 0"
          stroke="#9c3a1e"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Nose */}
      <path d="M60 64c-1.5 2-1.5 4 0 5" stroke="#eaa378" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  )
}